// How versions read, sort and are offered. Pure, so `versions.test.ts` can run it under Node. Name rules
// follow crates/shared/src/versions.rs, which decides in the end.
import type { Release } from "../api/types/Release";
import type { ReleaseRef } from "../api/types/ReleaseRef";
import type { ReleaseState } from "../api/types/ReleaseState";
import type { VersionCount } from "../api/types/VersionCount";

/** The server's default page. A shorter page is the last. */
export const RELEASE_PAGE = 200;

export function nextOffset(lastPageLength: number, lastOffset: number): number | undefined {
  return lastPageLength === RELEASE_PAGE ? lastOffset + RELEASE_PAGE : undefined;
}

/** An analytics row as `Android 2.4.0`. `unknown` stands in for a version nobody knows. */
export function versionCountLabel(row: VersionCount, unknown: string, showProject: boolean): string {
  const parts = [showProject ? row.project_key : "", row.app, row.version || unknown];
  return parts.filter(Boolean).join(" ");
}

const MAX_NAME_CHARS = 80;
const U64_MAX = BigInt("18446744073709551615");

type Parsed = { core: [bigint, bigint, bigint]; prerelease: string };

function number(part: string | undefined): bigint | null {
  if (part === undefined || !/^(0|[1-9][0-9]*)$/.test(part)) return null;
  const n = BigInt(part);
  return n <= U64_MAX ? n : null;
}

function validIdentifiers(text: string, numericRule: boolean): boolean {
  return text.split(".").every((id) => /^[0-9A-Za-z-]+$/.test(id) && (!numericRule || !/^[0-9]+$/.test(id) || id === "0" || !id.startsWith("0")));
}

/** `MAJOR.MINOR.PATCH[-prerelease][+build]`. A leading `v` or `2.4` isn't a version name. */
export function parseVersion(name: string): Parsed | null {
  const plus = name.indexOf("+");
  const rest = plus < 0 ? name : name.slice(0, plus);
  if (plus >= 0 && !validIdentifiers(name.slice(plus + 1), false)) return null;
  const dash = rest.indexOf("-");
  const core = dash < 0 ? rest : rest.slice(0, dash);
  const prerelease = dash < 0 ? "" : rest.slice(dash + 1);
  if (dash >= 0 && !validIdentifiers(prerelease, true)) return null;
  const parts = core.split(".");
  if (parts.length !== 3) return null;
  const [major, minor, patch] = parts.map(number);
  if (major == null || minor == null || patch == null) return null;
  return { core: [major, minor, patch], prerelease };
}

/** The form names compare in within an app: lowercase, with no leading `v` before a digit and no `+build`. */
export function normalizeVersion(name: string): string {
  const lower = name.trim().toLowerCase();
  const plus = lower.indexOf("+");
  const noBuild = plus < 0 ? lower : lower.slice(0, plus);
  return /^v[0-9]/.test(noBuild) ? noBuild.slice(1) : noBuild;
}

export type VersionNameError = "empty" | "not_semver" | "too_long";

/** What's wrong with a name for a new manual version, if anything. */
export function versionNameError(raw: string): VersionNameError | null {
  const name = raw.trim();
  if (!name) return "empty";
  if (!parseVersion(name)) return "not_semver";
  const plus = name.indexOf("+");
  return [...(plus < 0 ? name : name.slice(0, plus))].length > MAX_NAME_CHARS ? "too_long" : null;
}

function comparePrerelease(a: string, b: string): number {
  if (!a || !b) return a === b ? 0 : a ? -1 : 1;
  const left = a.split(".");
  const right = b.split(".");
  for (let i = 0; i < Math.max(left.length, right.length); i++) {
    const x = left[i];
    const y = right[i];
    if (x === undefined) return -1;
    if (y === undefined) return 1;
    if (x === y) continue;
    const [nx, ny] = [number(x), number(y)];
    if (nx != null && ny != null) return nx < ny ? -1 : 1;
    if (nx != null) return -1;
    if (ny != null) return 1;
    return x < y ? -1 : 1;
  }
  return 0;
}

function compareParsed(a: Parsed, b: Parsed): number {
  for (let i = 0; i < 3; i++) if (a.core[i] !== b.core[i]) return a.core[i]! < b.core[i]! ? -1 : 1;
  return comparePrerelease(a.prerelease, b.prerelease);
}

export type Orderable = { name: string; source?: string; released_at?: string | null };

/** When it came out: its date, or for a deployment version that only has a name, the date in the name. */
function outAt(v: Orderable): number | null {
  if (v.released_at) return Date.parse(v.released_at);
  const generated = /^(\d{4}-\d{2}-\d{2})-[0-9a-f]{8}$/.exec(v.name);
  return generated ? Date.parse(generated[1]!) : null;
}

/**
 * Newest first. Numbered versions keep their number order, so hotfix 2.3.9 stays below 2.4.0 whenever it came
 * out, and deployment versions and old names that can't be compared keep their date order. The two are merged by
 * date, with versions that haven't come out on top. Names with neither a number nor a date go last.
 */
export function sortVersions<T extends Orderable>(list: readonly T[]): T[] {
  const parsed = new Map(list.map((v) => [v, v.source === "deployment" ? null : parseVersion(normalizeVersion(v.name))]));
  const numbered = list.filter((v) => parsed.get(v)).sort((a, b) => compareParsed(parsed.get(b)!, parsed.get(a)!));
  const others = list.filter((v) => !parsed.get(v));
  const timed = others.filter((v) => outAt(v) != null).sort((a, b) => outAt(b)! - outAt(a)!);
  const rest = others.filter((v) => outAt(v) == null).sort((a, b) => b.name.localeCompare(a.name, undefined, { numeric: true }));
  const merged: T[] = [];
  let [n, t] = [0, 0];
  while (n < numbered.length && t < timed.length) {
    const at = outAt(numbered[n]!);
    merged.push(at == null || at >= outAt(timed[t]!)! ? numbered[n++]! : timed[t++]!);
  }
  return [...merged, ...numbered.slice(n), ...timed.slice(t), ...rest];
}

/** "Android 2.4.0", with the word for a version that was rolled back. */
export function versionLabel(v: { app_name: string; name: string; rolled_back_at?: string | null }, rolledBack?: string): string {
  const label = `${v.app_name} ${v.name}`;
  return v.rolled_back_at && rolledBack ? `${label} (${rolledBack})` : label;
}

/** Chips show the first two; the rest go behind "+N". */
export function chipView<T>(list: readonly T[]): { shown: T[]; rest: T[] } {
  return { shown: list.slice(0, 2), rest: list.slice(2) };
}

export type VersionMarker = "rolled_back" | "incomplete" | "needs_merge";

export function versionMarkers(r: Pick<Release, "rolled_back_at" | "contents_incomplete" | "needs_merge">): VersionMarker[] {
  const out: VersionMarker[] = [];
  if (r.rolled_back_at) out.push("rolled_back");
  if (r.contents_incomplete) out.push("incomplete");
  if (r.needs_merge) out.push("needs_merge");
  return out;
}

/** Notes stored when the version went out. A hand-added older version has none stored, so its notes still follow its items. */
export function notesFrozen(r: { state: ReleaseState; notes_edited: boolean }): boolean {
  return r.state !== "unreleased" && r.notes_edited;
}

export type PickVersion = {
  id: string;
  name: string;
  state: ReleaseState;
  app_id: string;
  app_name: string;
  source?: string;
  released_at?: string | null;
  rolled_back_at?: string | null;
};
export type VersionLock = "manager_only" | "archived";
export type PickGroup<T> = { app_id: string; app_name: string; versions: { version: T; lock: VersionLock | null }[] };

/**
 * A picker's rows, by app and newest first. Archived versions and versions of archived apps are left out unless
 * `keep` holds them, which is how a card's current versions stay visible. With `canManage` given, rows the person
 * can't change are locked: that's released ones for anyone but a manager, and archived ones for everybody.
 */
export function pickerGroups<T extends PickVersion>(
  list: readonly T[],
  { activeApps, canManage, query = "", keep = [], exclude }: { activeApps?: ReadonlySet<string>; canManage?: boolean; query?: string; keep?: readonly T[]; exclude?: string },
): PickGroup<T>[] {
  const q = query.trim().toLowerCase();
  const kept = new Set(keep.map((v) => v.id));
  const seen = new Set<string>();
  const groups = new Map<string, T[]>();
  for (const v of [...list, ...keep]) {
    if (seen.has(v.id) || v.id === exclude) continue;
    seen.add(v.id);
    const hidden = v.state === "archived" || (activeApps && !activeApps.has(v.app_id));
    if (hidden && !kept.has(v.id)) continue;
    if (q && !versionLabel(v).toLowerCase().includes(q)) continue;
    groups.set(v.app_id, [...(groups.get(v.app_id) ?? []), v]);
  }
  const lockOf = (v: T): VersionLock | null => {
    if (canManage === undefined) return null;
    if (v.state === "archived") return "archived";
    return v.state === "released" && !canManage ? "manager_only" : null;
  };
  return [...groups.entries()]
    .map(([app_id, versions]) => ({
      app_id,
      app_name: versions[0]!.app_name,
      versions: sortVersions(versions).map((version) => ({ version, lock: lockOf(version) })),
    }))
    .sort((a, b) => a.app_name.localeCompare(b.app_name, undefined, { sensitivity: "base" }));
}

export type VersionToggle = { kind: "set"; release_ids: string[] } | { kind: "remove"; release_id: string };

/**
 * What unticking or ticking a version means for a card or bug. `release_ids` replaces only the planned set and
 * leaves detected entries, so taking out a detected one uses the remove route, which stops a re-sync bringing it back.
 */
export function toggleVersion(current: readonly Pick<ReleaseRef, "id" | "planned" | "detected">[], id: string): VersionToggle {
  const planned = current.filter((r) => r.planned).map((r) => r.id);
  const entry = current.find((r) => r.id === id);
  if (!entry) return { kind: "set", release_ids: [...planned, id] };
  if (entry.detected) return { kind: "remove", release_id: id };
  return { kind: "set", release_ids: planned.filter((x) => x !== id) };
}

/** A date input's value as the moment a version came out: the start of that day where the person is. */
export function releasedAtFromDate(date: string): string {
  return new Date(`${date}T00:00`).toISOString();
}

/** Today as a date input's value, in local time. */
export function localToday(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
